import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Plus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { TaskLinkPicker, TaskLinkChip } from '../../components/admin/TaskLinks';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PT = 'America/Los_Angeles';
const PRIORITY = { high: 'bg-[#fde3d3] text-[#a4471b]', medium: 'bg-[#efeee6] text-[#5f6455]', low: 'bg-[#eef3e8] text-[#55704a]' };
const fmtDue = (d) => (d ? new Date(`${d}T12:00:00Z`).toLocaleDateString('en-US', { timeZone: PT, month: 'short', day: 'numeric' }) : 'No due date');
const todayPT = () => new Date().toLocaleDateString('en-CA', { timeZone: PT });

const NewTaskForm = ({ staff, headers, onSaved }) => {
  const [f, setF] = useState({ title: '', assigned_to: '', priority: 'medium', due_date: '', description: '' });
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim() || !f.assigned_to) { toast.error('Add a title and an owner'); return; }
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/admin/tasks`, { ...f, due_date: f.due_date || null, description: f.description || null, link_type: link?.type || null, link_id: link?.id || null }, { headers });
      setLink(null);
      toast.success('Task added');
      setF({ title: '', assigned_to: f.assigned_to, priority: 'medium', due_date: '', description: '' });
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not add the task'); } finally { setBusy(false); }
  };
  const input = 'h-10 rounded-md border border-[#dcded2] bg-white px-3 text-sm';
  return (
    <form onSubmit={submit} className="rounded-[10px] bg-[#fbfaf7] border border-[#e7e4db] p-4 grid gap-3 md:grid-cols-[2fr_1fr_120px_150px_auto] items-end" data-testid="task-new-form">
      <label className="text-[10px] font-bold tracking-wider text-[#7a7c6d]">TASK<input className={`${input} w-full mt-1`} maxLength={200} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="task-new-title" /></label>
      <label className="text-[10px] font-bold tracking-wider text-[#7a7c6d]">OWNER
        <select className={`${input} w-full mt-1`} value={f.assigned_to} onChange={(e) => setF({ ...f, assigned_to: e.target.value })} data-testid="task-new-owner">
          <option value="">Choose…</option>{staff.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
        </select></label>
      <label className="text-[10px] font-bold tracking-wider text-[#7a7c6d]">PRIORITY
        <select className={`${input} w-full mt-1`} value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} data-testid="task-new-priority">
          <option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
        </select></label>
      <label className="text-[10px] font-bold tracking-wider text-[#7a7c6d]">DUE<input type="date" className={`${input} w-full mt-1`} value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="task-new-due" /></label>
      <div className="md:col-span-4"><p className="text-[10px] font-bold tracking-wider text-[#7a7c6d] mb-1">LINK TO A RECORD (OPTIONAL)</p><TaskLinkPicker headers={headers} value={link} onChange={setLink} testid="task-new-link" /></div>
      <button disabled={busy} className="h-10 px-4 rounded-[5px] bg-[#f36a19] text-[#171912] text-xs font-bold inline-flex items-center justify-center gap-1.5 md:self-start md:mt-5" data-testid="task-new-submit"><Plus className="h-3.5 w-3.5" />Add task</button>
    </form>
  );
};

const TaskRow = ({ t, headers, onChanged }) => {
  const done = t.status === 'done';
  const [linking, setLinking] = useState(false);
  const setTaskLink = async (v) => {
    try {
      await axios.patch(`${API_URL}/api/tasks/${t.id}`, v ? { link_type: v.type, link_id: v.id } : { clear_link: true }, { headers });
      toast.success(v ? 'Record linked' : 'Link removed'); setLinking(false); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update the link'); }
  };
  const overdue = !done && t.due_date && t.due_date < todayPT();
  const toggle = async () => {
    try { await axios.patch(`${API_URL}/api/tasks/${t.id}`, { status: done ? 'todo' : 'done' }, { headers }); onChanged(); toast.success(done ? 'Task reopened' : 'Task completed'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not update the task'); }
  };
  return (
    <li className="flex flex-wrap sm:flex-nowrap items-start sm:items-center gap-x-5 gap-y-2 py-5 px-2 border-b border-[#ecebe4] last:border-0" data-testid={`task-row-${t.id}`}>
      <input type="checkbox" checked={done} onChange={toggle} className="h-4 w-4 mt-1 sm:mt-0 accent-[#f36a19]" aria-label={done ? 'Reopen task' : 'Complete task'} data-testid={`task-toggle-${t.id}`} />
      <div className="flex-1 min-w-[200px]">
        <p className={`text-[15px] font-semibold ${done ? 'line-through text-[#9a9b90]' : 'text-[#20211d]'}`}>{t.title}</p>
        <p className="text-xs text-[#77776e] mt-1">{t.description || `Set by ${t.assigned_by_display}`}{t.source_label ? ` · from ${t.source_label}` : ''}</p>
        <div className="flex flex-wrap items-center gap-3 mt-1.5">
          <TaskLinkChip link={t.link} testid={`task-link-${t.id}`} />
          <button type="button" onClick={() => setLinking((o) => !o)} className="text-[11px] text-[#5f6455] underline" data-testid={`task-link-edit-${t.id}`}>{t.link ? 'Change link' : 'Link a record'}</button>
          {t.link && <button type="button" onClick={() => setTaskLink(null)} className="text-[11px] text-[#5f6455] underline" data-testid={`task-link-remove-${t.id}`}>Remove link</button>}
        </div>
        {linking && <div className="mt-2 max-w-lg"><TaskLinkPicker headers={headers} value={null} onChange={setTaskLink} testid={`task-link-picker-${t.id}`} /></div>}
      </div>
      <div className="w-full sm:w-auto pl-9 sm:pl-0 sm:text-right shrink-0">
        <span className={`text-[10px] px-2 py-0.5 rounded ${PRIORITY[t.priority] || PRIORITY.medium}`}>{(t.priority || 'medium').replace(/^./, (c) => c.toUpperCase())}</span>
        <p className={`text-[11px] mt-1.5 ${overdue ? 'text-[#b4441b] font-semibold' : 'text-[#77776e]'}`} data-testid={`task-due-${t.id}`}>{t.assigned_to_display} · {overdue ? 'Overdue · ' : ''}{fmtDue(t.due_date)}</p>
      </div>
    </li>
  );
};

export default function AdminTasksPage() {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [rows, setRows] = useState(null);
  const [staff, setStaff] = useState([]);
  const [filter, setFilter] = useState('open');
  const [owner, setOwner] = useState('');
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [counts, setCounts] = useState(null);
  const load = useCallback(() => {
    const q = new URLSearchParams({ status: filter, ...(owner ? { assignee: owner } : {}) });
    axios.get(`${API_URL}/api/admin/tasks?${q}`, { headers }).then((r) => setRows(r.data.tasks)).catch((e) => setError(e.response?.status === 403 ? 'Managing team tasks needs the task-management permission. Ask a master admin to grant it.' : 'Tasks could not be loaded.'));
    axios.get(`${API_URL}/api/admin/tasks?status=open${owner ? `&assignee=${owner}` : ''}`, { headers }).then((r) => {
      const t = todayPT(); const open = r.data.tasks;
      setCounts({ open: open.length, overdue: open.filter((x) => x.due_date && x.due_date < t).length, today: open.filter((x) => x.due_date === t).length });
    }).catch(() => {});
  }, [headers, filter, owner]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { axios.get(`${API_URL}/api/admin/tasks/assignees`, { headers }).then((r) => setStaff(r.data)).catch(() => {}); }, [headers]);
  const sel = 'h-10 rounded-md border border-[#dcded2] bg-white px-2 text-sm mt-1 block';
  return (
    <main className="max-w-[1400px] mx-auto px-5 sm:px-9 py-8" data-testid="tasks-page">
      <p className="text-[10px] tracking-[0.16em] font-bold text-[#7a7c6d]">GLEAM GURU OPERATIONS</p>
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-[#20211d] mt-3">Tasks<span className="text-[#939681] font-normal">.</span></h1>
      <p className="text-sm text-[#77776e] mt-3">One shared list for the work between appointments.</p>
      {error ? <p className="mt-8 text-sm text-[#a4471b]" data-testid="tasks-error">{error}</p> : (
        <>
          <div className="mt-8 py-5 border-y border-[#e3e0d6] flex flex-wrap items-center gap-8" data-testid="tasks-counts">
            {[['open', 'open'], ['overdue', 'overdue'], ['today', 'due today']].map(([k, l]) => (
              <span key={k} className="text-[#77776e] text-sm"><b className="text-2xl font-normal text-[#20211d] mr-2" data-testid={`tasks-count-${k}`}>{counts ? counts[k] : '–'}</b>{l}</span>
            ))}
            <button onClick={() => setAdding((a) => !a)} className="ml-auto h-10 px-4 rounded-[5px] bg-[#f36a19] text-[#171912] text-xs font-bold inline-flex items-center gap-1.5" data-testid="tasks-add-toggle"><Plus className="h-3.5 w-3.5" />Add task</button>
          </div>
          {adding && <div className="mt-5"><NewTaskForm staff={staff} headers={headers} onSaved={load} /></div>}
          <div className="flex flex-wrap items-end gap-4 mt-6">
            <label className="text-xs text-[#5f6455]">Owner
              <select value={owner} onChange={(e) => setOwner(e.target.value)} className={sel} data-testid="tasks-owner-filter">
                <option value="">All</option>{staff.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
              </select></label>
            <label className="text-xs text-[#5f6455]">Show
              <select value={filter} onChange={(e) => setFilter(e.target.value)} className={sel} data-testid="tasks-show-filter">
                <option value="open">Open</option><option value="done">Completed</option><option value="all">All</option>
              </select></label>
          </div>
          <section className="mt-5 rounded-[10px] bg-[#fbfaf7] border border-[#e7e4db] px-5">
            {!rows ? <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#f36a19]" /></div>
              : rows.length === 0 ? <p className="py-10 text-center text-sm text-[#77776e]" data-testid="tasks-empty">No tasks here.</p>
              : <ul>{rows.map((t) => <TaskRow key={t.id} t={t} headers={headers} onChanged={load} />)}</ul>}
          </section>
        </>
      )}
    </main>
  );
}
